import React, { useEffect, useState } from 'react';

import { climbBridgeAPI, toolsAPI } from '../../../lib/api';
import PlaybackTransport, { SPEEDS, formatDuration, parseDuration } from './PlaybackTransport';

const TOOL_KEY = 'climb-bridge';
const FIELD = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';
const LABEL = 'block text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wide';
const QUIET_BTN = 'px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#111111] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-40 transition-colors';

/** Pick "running" to reuse the tokens of the bridge running for the show now. */
const RUNNING = '__running__';

function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * Screen 4 — Recordings.
 *
 * Record a live competition, then play it back into the same Singular streams
 * between events, as though it were happening again. Playback runs the bridge
 * from the recording rather than ClimbAlong, so lane switching and athlete
 * selection on the Live screen work exactly as they do on the night.
 */
export default function RecordingsPanel({ show, summary, onChanged, onPlaybackStarted }) {
  const [recordings, setRecordings] = useState(null);
  const [presets, setPresets] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [playing, setPlaying] = useState(null);   // { id, source, speed, loop }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importing, setImporting] = useState(false);
  // { id, activity, from, to, name } — the trim being set up, if any.
  const [trimming, setTrimming] = useState(null);
  const [now, setNow] = useState(Date.now());

  const showId = show?.id || '';
  const running = !!summary?.running;
  const mode = summary?.health?.bridge?.mode;
  const playback = running && mode === 'playback' ? summary.health.bridge.playback : null;
  const recording = running && mode !== 'playback' ? summary?.health?.bridge?.recording : null;
  const liveRunning = running && mode !== 'playback';

  function load() {
    return climbBridgeAPI.recordings()
      .then(res => setRecordings(res.data.recordings || []))
      .catch(err => setError(err.response?.data?.error || 'Could not load recordings'));
  }

  useEffect(() => {
    load();
    toolsAPI.presets(TOOL_KEY).then(res => setPresets(res.data || [])).catch(() => setPresets([]));
    // Keeps the size and length of a recording in progress current in the list.
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!recording) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [!!recording]);

  /** Save a recording to the operator's own disk, under the name it has here. */
  async function download(rec) {
    setBusy(`download-${rec.id}`);
    setError('');
    try {
      const res = await climbBridgeAPI.exportRecording(rec.id);
      const match = /filename="([^"]+)"/.exec(res.headers?.['content-disposition'] || '');
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = match?.[1] || `${rec.name}.climbrec.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoked on the next tick: Safari cancels a download whose URL is gone.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not download that recording');
    } finally {
      setBusy('');
    }
  }

  async function importFile(file) {
    if (!file) return;
    setImporting(true);
    setError('');
    try {
      await climbBridgeAPI.importRecording(file);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not import that file');
    } finally {
      setImporting(false);
    }
  }

  /**
   * Open the trim panel for a recording, and look up where its data actually
   * is. A bridge nobody stopped leaves hours of an empty hall on the end, and
   * finding that by scrubbing is the job this saves.
   */
  async function openTrim(rec) {
    setTrimming({ id: rec.id, activity: null, from: '0:00', to: '', name: `${rec.name} (trimmed)` });
    setError('');
    try {
      const res = await climbBridgeAPI.recordingActivity(rec.id);
      setTrimming(t => (t && t.id === rec.id
        ? { ...t, activity: res.data, to: formatDuration(res.data.suggested.to_ms) }
        : t));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not read that recording');
      setTrimming(null);
    }
  }

  async function runTrim() {
    const fromMs = parseDuration(trimming.from);
    const toMs = parseDuration(trimming.to);
    if (fromMs == null || toMs == null) {
      setError('Times go in as h:mm:ss, for example 3:12:00.');
      return;
    }
    const ok = await act(`trim-${trimming.id}`, () => climbBridgeAPI.trimRecording(trimming.id, {
      from_ms: fromMs,
      to_ms: toMs,
      ...(trimming.name.trim() ? { name: trimming.name.trim() } : {}),
    }));
    if (ok) setTrimming(null);
  }

  async function act(name, fn) {
    setBusy(name);
    setError('');
    try {
      await fn();
      await Promise.all([load(), onChanged?.()]);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'That did not work');
      return false;
    } finally {
      setBusy('');
    }
  }

  function openPlay(rec) {
    // The obvious streams first: the ones in use right now, then the saved event
    // the recording was made with, then any saved event at all.
    const fromRecording = presets.find(p => p.id === rec.preset_id);
    const source = running ? RUNNING : (fromRecording?.id || presets[0]?.id || '');
    setPlaying({ id: rec.id, source, speed: 1, loop: false });
  }

  async function startPlay(rec) {
    const ok = await act(`play-${rec.id}`, () => climbBridgeAPI.startPlayback({
      show_id: showId,
      recording_id: rec.id,
      ...(playing.source && playing.source !== RUNNING ? { preset_id: playing.source } : {}),
      speed: playing.speed,
      loop: playing.loop,
    }));
    if (ok) {
      setPlaying(null);
      onPlaybackStarted?.();
    }
  }

  const recordingElapsed = recording
    ? Math.max(recording.durationMs || 0, now - new Date(recording.startedAt).getTime())
    : 0;

  return (
    <div className="space-y-6">
      {error && <div className="px-4 py-3 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">{error}</div>}

      {/* What is happening now */}
      {playback ? (
        <PlaybackTransport
          showId={showId}
          playback={playback}
          stopping={busy === 'stop-playback'}
          onStop={() => act('stop-playback', () => climbBridgeAPI.stop(showId))}
        />
      ) : liveRunning ? (
        recording ? (
          <div className="flex items-center gap-4 px-4 py-3 bg-red-950/30 border border-red-800 rounded-lg">
            <span className="relative flex h-3 w-3 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-red-400">Recording</div>
              <div className="text-sm text-white truncate">{recording.name}</div>
              <div className="text-[11px] text-gray-400 tabular-nums">
                {formatDuration(recordingElapsed)} · {formatBytes(recording.bytes)} stored
                {recording.pendingChunks > 0 && <span className="text-amber-400"> · {recording.pendingChunks} chunk(s) waiting for the database</span>}
                {recording.lastError && <span className="text-amber-400"> · {recording.lastError}</span>}
              </div>
            </div>
            <button
              onClick={() => act('stop-recording', () => climbBridgeAPI.stopRecording(showId))}
              disabled={busy === 'stop-recording'}
              className="flex-shrink-0 px-4 py-2 text-sm rounded bg-[#1a1a1a] border border-[#3a3a3a] text-white hover:border-[#4a4a4a] disabled:opacity-40"
            >
              {busy === 'stop-recording' ? 'Saving…' : 'Stop recording'}
            </button>
          </div>
        ) : (
          <div className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded-lg">
            <div className="text-sm text-white mb-2">The bridge is live but not recording.</div>
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Name (optional) — defaults to the competition, round and time"
                className={FIELD}
              />
              <button
                onClick={() => act('start-recording', () => climbBridgeAPI.startRecording(showId, newName.trim())).then(ok => ok && setNewName(''))}
                disabled={busy === 'start-recording'}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm rounded bg-red-700 hover:bg-red-600 text-white disabled:opacity-40"
              >
                <span className="w-2 h-2 rounded-full bg-white" />
                Record
              </button>
            </div>
          </div>
        )
      ) : (
        <div className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded-lg text-xs text-gray-400 leading-relaxed">
          Start the bridge on <strong className="text-gray-300">Setup</strong> to record a competition — it records from the
          moment it starts unless you untick that. Or play a recording below: it goes out to Singular as though the round
          were live, with no ClimbAlong connection needed.
        </div>
      )}

      {/* The library */}
      <section>
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <h2 className="text-sm font-semibold text-white">Recordings</h2>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-600">Kept in the database; download one to keep a copy of your own</span>
            <label className={`${QUIET_BTN} cursor-pointer`}>
              {importing ? 'Importing…' : 'Import a file'}
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                disabled={importing}
                onChange={e => { importFile(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>

        {recordings === null ? (
          <div className="px-4 py-6 text-xs text-gray-600 text-center">Loading…</div>
        ) : recordings.length === 0 ? (
          <div className="px-4 py-6 bg-[#111111] border border-[#2a2a2a] rounded text-xs text-gray-500 text-center">
            No recordings yet.
          </div>
        ) : (
          <div className="space-y-2">
            {recordings.map(rec => {
              const lanes = Array.isArray(rec.lanes) ? rec.lanes : [];
              const rounds = [...new Set(lanes.map(l => l.roundTitle).filter(Boolean))];
              const isPlaying = playback?.recordingId === rec.id || rec.playing;
              const isRecording = rec.status === 'recording';
              return (
                <div key={rec.id} className={`bg-[#111111] border rounded ${isPlaying ? 'border-blue-800' : 'border-[#2a2a2a]'}`}>
                  <div className="flex items-start gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      {renaming?.id === rec.id ? (
                        <form
                          onSubmit={e => {
                            e.preventDefault();
                            act(`rename-${rec.id}`, () => climbBridgeAPI.renameRecording(rec.id, renaming.name.trim())).then(ok => ok && setRenaming(null));
                          }}
                          className="flex gap-2"
                        >
                          <input autoFocus value={renaming.name} onChange={e => setRenaming({ ...renaming, name: e.target.value })} className={FIELD} />
                          <button type="submit" disabled={!renaming.name.trim()} className={QUIET_BTN}>Save</button>
                          <button type="button" onClick={() => setRenaming(null)} className={QUIET_BTN}>Cancel</button>
                        </form>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-white truncate" title={rec.name}>{rec.name}</span>
                          {isRecording && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/40 text-red-400 flex-shrink-0">recording</span>}
                          {isPlaying && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300 flex-shrink-0">playing</span>}
                          {rec.status === 'interrupted' && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-400 flex-shrink-0"
                              title="Cut off by a restart or a storage failure. Everything stored up to then plays."
                            >
                              cut short
                            </span>
                          )}
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {rec.competition_title || `Competition ${rec.competition_id}`}
                        {rounds.length > 0 && <> · {rounds.join(', ')}</>}
                      </div>
                      <div className="text-[11px] text-gray-600 mt-0.5 tabular-nums">
                        {formatWhen(rec.started_at)} · {formatDuration(rec.duration_ms)} · {formatBytes(rec.bytes)}
                        {lanes.length > 0 && <> · {lanes.length} lane{lanes.length === 1 ? '' : 's'}</>}
                      </div>
                    </div>

                    {renaming?.id !== rec.id && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => (playing?.id === rec.id ? setPlaying(null) : openPlay(rec))}
                          disabled={isRecording || !showId}
                          title={!showId ? 'Pick a show first' : isRecording ? 'Stop recording before playing it' : undefined}
                          className="px-3 py-1.5 text-xs rounded bg-blue-700 hover:bg-blue-600 text-white disabled:opacity-40"
                        >
                          ▶ Play
                        </button>
                        <button
                          onClick={() => (trimming?.id === rec.id ? setTrimming(null) : openTrim(rec))}
                          disabled={rec.status === 'recording'}
                          title="Make a shorter copy, keeping the part you want"
                          className={QUIET_BTN}
                        >
                          Trim
                        </button>
                        <button
                          onClick={() => download(rec)}
                          disabled={busy === `download-${rec.id}`}
                          title="Save this recording to your computer"
                          className={QUIET_BTN}
                        >
                          {busy === `download-${rec.id}` ? 'Saving…' : 'Download'}
                        </button>
                        <button onClick={() => setRenaming({ id: rec.id, name: rec.name })} className={QUIET_BTN}>Rename</button>
                        <button
                          onClick={() => setConfirmDelete(confirmDelete === rec.id ? null : rec.id)}
                          disabled={isRecording || isPlaying}
                          className={`${QUIET_BTN} hover:text-red-400`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {confirmDelete === rec.id && (
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-[#2a2a2a] bg-red-950/20">
                      <span className="text-xs text-red-300">Delete this recording for good?</span>
                      <div className="flex gap-2">
                        <button onClick={() => setConfirmDelete(null)} className={QUIET_BTN}>Keep it</button>
                        <button
                          onClick={() => act(`delete-${rec.id}`, () => climbBridgeAPI.deleteRecording(rec.id)).then(() => setConfirmDelete(null))}
                          disabled={busy === `delete-${rec.id}`}
                          className="px-3 py-1.5 text-xs rounded bg-red-700 hover:bg-red-600 text-white disabled:opacity-40"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}

                  {trimming?.id === rec.id && (
                    <div className="px-4 py-3 border-t border-[#2a2a2a] bg-[#0c0c0c] space-y-3">
                      {!trimming.activity ? (
                        <p className="text-xs text-gray-500">Looking through the recording for where the data is…</p>
                      ) : (
                        <>
                          <div className="text-xs text-gray-400 leading-relaxed">
                            {trimming.activity.last_change_ms == null ? (
                              <>Nothing in this recording ever changed, so there is no event in it to keep.</>
                            ) : (
                              <>
                                The data stops at <strong className="text-gray-200">{formatDuration(trimming.activity.last_change_ms)}</strong>
                                {trimming.activity.idle_tail_ms > 60000 && (
                                  <> , leaving <strong className="text-gray-200">{formatDuration(trimming.activity.idle_tail_ms)}</strong> of nothing after it</>
                                )}
                                . Full length {formatDuration(trimming.activity.duration_ms)}.
                              </>
                            )}
                          </div>

                          {/* Where the changes are, so the kept part can be seen at a glance */}
                          <div className="flex items-end gap-px h-8" title="Where the data changes over the recording">
                            {trimming.activity.buckets.map((b, i) => {
                              const peak = Math.max(...trimming.activity.buckets.map(x => x.changes), 1);
                              const inRange = b.fromMs >= (parseDuration(trimming.from) ?? 0) && b.toMs <= ((parseDuration(trimming.to) ?? trimming.activity.duration_ms) + 1);
                              return (
                                <div
                                  key={i}
                                  className={`flex-1 ${inRange ? 'bg-blue-600' : 'bg-[#2a2a2a]'}`}
                                  style={{ height: `${Math.max(2, Math.round((b.changes / peak) * 100))}%` }}
                                />
                              );
                            })}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <label className={LABEL}>Keep from</label>
                              <input value={trimming.from} onChange={e => setTrimming({ ...trimming, from: e.target.value })} placeholder="0:00" className={FIELD} />
                            </div>
                            <div>
                              <label className={LABEL}>Keep to</label>
                              <input value={trimming.to} onChange={e => setTrimming({ ...trimming, to: e.target.value })} placeholder={formatDuration(trimming.activity.duration_ms)} className={FIELD} />
                            </div>
                            <div>
                              <label className={LABEL}>New recording called</label>
                              <input value={trimming.name} onChange={e => setTrimming({ ...trimming, name: e.target.value })} className={FIELD} />
                            </div>
                          </div>

                          <p className="text-[10px] text-gray-600 leading-relaxed">
                            Times are h:mm:ss from the start. This makes a new recording and leaves this one exactly as it is, so
                            you can trim again. Cutting only the end is quick; cutting the start rewrites the kept part and takes longer.
                          </p>

                          <div className="flex justify-end gap-2">
                            <button onClick={() => setTrimming(null)} className={QUIET_BTN}>Cancel</button>
                            <button
                              onClick={runTrim}
                              disabled={busy === `trim-${rec.id}`}
                              className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40"
                            >
                              {busy === `trim-${rec.id}` ? 'Trimming…' : 'Make trimmed copy'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {playing?.id === rec.id && (
                    <div className="px-4 py-3 border-t border-[#2a2a2a] bg-[#0c0c0c] space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <label className={LABEL}>Publish to</label>
                          <select value={playing.source} onChange={e => setPlaying({ ...playing, source: e.target.value })} className={FIELD}>
                            {running && <option value={RUNNING}>The streams the bridge is using now</option>}
                            {presets.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.name}{p.id === rec.preset_id ? ' (recorded with)' : ''}
                              </option>
                            ))}
                            {!running && presets.length === 0 && <option value="">No saved events</option>}
                          </select>
                        </div>
                        <div>
                          <label className={LABEL}>Speed</label>
                          <select value={playing.speed} onChange={e => setPlaying({ ...playing, speed: Number(e.target.value) })} className={FIELD}>
                            {SPEEDS.map(s => <option key={s} value={s}>{s}×{s === 1 ? ' (real time)' : ''}</option>)}
                          </select>
                        </div>
                      </div>

                      <label className="flex items-center gap-2 text-xs text-gray-400">
                        <input type="checkbox" checked={playing.loop} onChange={e => setPlaying({ ...playing, loop: e.target.checked })} />
                        Loop — start again at the end instead of holding the last picture
                      </label>

                      {!running && presets.length === 0 && (
                        <p className="text-[11px] text-amber-400">
                          Playback needs the Singular data stream tokens to send to. Save the event on Setup, then play into it.
                        </p>
                      )}
                      {liveRunning && (
                        <p className="text-[11px] text-amber-400">
                          This stops the live bridge on {show?.name || 'this show'}{recording ? ' and saves its recording' : ''}. Singular
                          will show the recording instead of live data until you stop playback and start the bridge again.
                        </p>
                      )}

                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPlaying(null)} className={QUIET_BTN}>Cancel</button>
                        <button
                          onClick={() => startPlay(rec)}
                          disabled={busy === `play-${rec.id}` || !playing.source}
                          className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40"
                        >
                          {busy === `play-${rec.id}` ? 'Loading…' : `Play into ${show?.name || 'show'}`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
